import React from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';

const OtherTripFields = ({ formData, setFormData, tripType, showDates = true }) => {
  const isHotelStay = tripType === 'hotel_stay';
  const isUkHotelStay = tripType === 'uk_hotel_stay';
  const isCourseUk = tripType === 'course_uk';
  const isCourseForeign = tripType === 'course_foreign';
  const isCourse = isCourseUk || isCourseForeign;
  const isQualifyingHoliday = tripType === 'qualifying_holiday';
  
  // Show continuous trip options for foreign trips
  const showContinuousOptions = isHotelStay || isCourseForeign || isQualifyingHoliday;

  return (
    <>
      {/* Date Fields - only show if showDates is true */}
      {showDates && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">
                {isHotelStay || isUkHotelStay ? 'Check-in Date' : isCourse ? 'Start Date' : 'Date Left UK'} *
              </Label>
              <Input
                type="date"
                value={formData.date_left_uk}
                onChange={(e) => setFormData({ ...formData, date_left_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                required
                data-testid="date-left-uk-input"
              />
              {showContinuousOptions && (
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    id="no_uk_departure_other"
                    checked={formData.no_uk_departure}
                    onCheckedChange={(checked) => setFormData({ ...formData, no_uk_departure: checked })}
                    className="border-cyan-500 data-[state=checked]:bg-cyan-500 h-3 w-3"
                    data-testid="no-uk-departure-checkbox"
                  />
                  <Label htmlFor="no_uk_departure_other" className="text-xs text-cyan-400 cursor-pointer">
                    Already abroad (continuous trip)
                  </Label>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">
                {isHotelStay || isUkHotelStay ? 'Check-out Date' : isCourse ? 'End Date' : 'Date Returned UK'} *
              </Label>
              <Input
                type="date"
                value={formData.date_returned_uk}
                onChange={(e) => setFormData({ ...formData, date_returned_uk: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white"
                required
                data-testid="date-returned-uk-input"
              />
              {showContinuousOptions && (
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    id="no_uk_return_other"
                    checked={formData.no_uk_return}
                    onCheckedChange={(checked) => setFormData({ ...formData, no_uk_return: checked })}
                    className="border-cyan-500 data-[state=checked]:bg-cyan-500 h-3 w-3"
                    data-testid="no-uk-return-checkbox"
                  />
                  <Label htmlFor="no_uk_return_other" className="text-xs text-cyan-400 cursor-pointer">
                    Stayed abroad (continuous trip)
                  </Label>
                </div>
              )}
            </div>
          </div>

          {/* Continuous trip info banner */}
          {showContinuousOptions && (formData.no_uk_departure || formData.no_uk_return) && (
            <div className="p-3 bg-cyan-500/10 rounded-lg border border-cyan-500/30">
              <p className="text-sm text-cyan-400">
                <strong>Continuous Trip:</strong> {formData.no_uk_departure && !formData.no_uk_return && "You were already abroad before this stay."}
                {!formData.no_uk_departure && formData.no_uk_return && "You stayed abroad after this stay."}
                {formData.no_uk_departure && formData.no_uk_return && "This entire stay was part of a continuous period abroad."}
              </p>
            </div>
          )}
        </>
      )}

      {/* Hotel Stay Fields */}
      {(isHotelStay || isUkHotelStay) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Hotel Name</Label>
            <Input
              value={formData.hotel_name}
              onChange={(e) => setFormData({ ...formData, hotel_name: e.target.value })}
              placeholder={isUkHotelStay ? "e.g., Premier Inn" : "e.g., Hilton Dubai"}
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">City *</Label>
            <Input
              value={formData.hotel_city}
              onChange={(e) => setFormData({ ...formData, hotel_city: e.target.value })}
              placeholder={isUkHotelStay ? "e.g., Aberdeen" : "e.g., Dubai"}
              className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              required
            />
          </div>
        </div>
      )}

      {isUkHotelStay && (
        <div className="space-y-2">
          <Label className="text-slate-300">Reason for Stay</Label>
          <Input
            value={formData.hotel_reason}
            onChange={(e) => setFormData({ ...formData, hotel_reason: e.target.value })}
            placeholder="e.g., Waiting for vessel"
            className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
          />
        </div>
      )}

      {/* Course Fields */}
      {isCourse && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Course Name *</Label>
              <Input
                value={formData.course_name}
                onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
                placeholder="e.g., STCW Refresher"
                className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Course Provider</Label>
              <Input
                value={formData.course_provider}
                onChange={(e) => setFormData({ ...formData, course_provider: e.target.value })}
                placeholder="e.g., Clyde Training"
                className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
          </div>
          {isCourseForeign && (
            <div className="space-y-2">
              <Label className="text-slate-300">Location</Label>
              <Input
                value={formData.course_location}
                onChange={(e) => setFormData({ ...formData, course_location: e.target.value })}
                placeholder="e.g., Rotterdam, Netherlands"
                className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
          )}
        </>
      )}

      {/* Qualifying Holiday Location Field */}
      {isQualifyingHoliday && (
        <div className="space-y-2">
          <Label className="text-slate-300">Destination(s)</Label>
          <Input
            value={formData.foreign_ports}
            onChange={(e) => setFormData({ ...formData, foreign_ports: e.target.value })}
            placeholder="e.g., Spain, Portugal (comma separated for multiple)"
            className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500"
            data-testid="holiday-destination-input"
          />
          <p className="text-xs text-slate-500">
            Enter the country or location of your qualifying holiday
          </p>
        </div>
      )}
    </>
  );
};

export default OtherTripFields;
